
import { dataModule } from '@/store/data';
import { WatchList } from '@/api';
import NTable from '@/uikit/table/NTable.vue';
import { columns } from './watch-lists-columns';
import SettingsBar from '@/components/common/SettingsBar.vue';
import { ITableCellChangePayload } from '@/uikit/table/NTableCell.vue';
import NLoadingCircle from '@/uikit/loading/NLoadingCircle.vue';
import SettingsPageLayout from '@/pages/settings/SettingsPageLayout.vue';
import NForm from '@/uikit/forms/NForm.vue';
import NTabs from '@/uikit/tabs/NTabs.vue';
import { Options, Vue } from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { aclModule } from '@/store/acl';
import { ItemsActionNames } from '@/definitions/app/item.actions.name';
import { globalEventModule, GlobalEventName } from '@/store/global-event';
import { multisidebarModule } from '@/store/multisidebar';
import { GlobalEvent } from '@/store/global-event/types';
import { merge } from 'lodash';
import { MultisidebarItemTypes } from '@/store/multisidebar/types';
import { actionHandler } from '@/store/data/ActionHandler';

const AclModelName = 'watchlist';
const AclItemPropertyName = 'watch_list_permissions';

const ActionPathNames = {
  Name: 'name',
  Active: 'active'
};

function filterItemsByName(items: WatchList[], query: string) {
  const re = new RegExp(query, 'i');
  return items.filter((item) => re.test(item.name));
}

function sortByField(fieldName: any) {
  let [field, direction] = fieldName.charAt(0) === '-' ? [fieldName.slice(1), -1] : [fieldName, 1];
  return function (a: any, b: any) {
    return a[field].localeCompare(b[field]) * direction;
  };
}

const SidebarType = MultisidebarItemTypes.WatchLists;

@Options({
  components: {
    SettingsPageLayout,
    NLoadingCircle,
    SettingsBar,
    NTable,
    NForm,
    NTabs
  }
})
export default class WatchListsPage extends Vue {
  sortField = '';
  searchQuery = '';

  get module() {
    dataModule.watchListsModule.aclModelName = AclModelName;
    return dataModule.watchListsModule;
  }

  get items() {
    let items = this.searchQuery.length > 0 ? filterItemsByName(this.module.items, this.searchQuery) : this.module.items;
    this.sortField && items.sort(sortByField(this.sortField));
    return items;
  }

  get columns() {
    return columns;
  }

  get selectedItemsIds() {
    return this.sidebarModule.getItemsByType(SidebarType).map((v) => v.model.item.id);
  }

  get lastPageEvent() {
    return globalEventModule.current?.type === SidebarType ? globalEventModule.current : null;
  }

  get sidebarModule() {
    return multisidebarModule;
  }

  get modelAcl() {
    return aclModule.getModelAcl(this.module);
  }

  get maxAcl() {
    return { view: this.modelAcl.view, update: this.modelAcl.update, delete: this.modelAcl.delete };
  }

  get itemsAcl() {
    return this.items.map((item) => ({
      itemId: item.id,
      acl: aclModule.getItemAclByUserProperty(item.id, AclItemPropertyName, this.maxAcl)
    }));
  }

  get disabledItemIds() {
    return this.itemsAcl.filter(({ acl }) => !acl.update).map(({ itemId }) => itemId);
  }

  @Watch('lastPageEvent')
  handleGlobalEvent(event: GlobalEvent) {
    if (!event) return;
    switch (event.name) {
      case GlobalEventName.Create:
      case GlobalEventName.Delete:
        this.module.getStatistics();
        break;
      case GlobalEventName.Update:
        merge(
          this.module.items.find((v) => v.id === event.payload.id),
          event.payload
        );
        break;
    }
  }

  mounted() {
    this.module.get();
  }

  handleTableAction(payload: ITableCellChangePayload) {
    if (payload.type === 'change') {
      switch (payload.path) {
        case ActionPathNames.Active:
          actionHandler.run(ItemsActionNames.UpdateActiveField, { type: SidebarType, rawItem: payload.row });
          break;
        case ActionPathNames.Name:
          actionHandler.run(ItemsActionNames.ShowItem, { type: SidebarType, rawItem: payload.row });
      }
    }
  }

  handleTableSort(columnName: string) {
    if (columnName === 'name') {
      this.sortField = this.sortField === columnName ? `-${columnName}` : columnName;
    }
  }

  handleSelectChanges(changesSelectionIds: number[]) {
    this.module.items
      .filter((v) => changesSelectionIds.includes(v.id))
      .forEach((v) => {
        actionHandler.run(ItemsActionNames.ToggleSelectItem, { type: SidebarType, rawItem: v });
      });
  }

  handleCreate() {
    this.sidebarModule.addNewItemByType(SidebarType);
  }
}
